import React, { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Box, Flex, Group, Input, Popover, UnstyledButton, rem } from '@mantine/core';
import type { FieldValues, Path, UseFormRegister, UseFormWatch } from 'react-hook-form';
import {
  type CalendarDate,
  type DateValue,
  getLocalTimeZone,
  isToday as isSameCalendarDayAsToday,
  parseDate,
} from '@internationalized/date';
import { useCalendar, useCalendarCell, useCalendarGrid, useLocale } from 'react-aria';
import { useCalendarState } from 'react-stately';

import { createCalendar } from '@/components/content/date/datepicker/createCalendar';
import ChevronLeft from '@/assets/icons/content/chevron-left.svg';
import ChevronRight from '@/assets/icons/content/chevron-right.svg';

import classes from '../Form.module.css';
import calendarClasses from '@/components/content/date/datepicker/DatePicker.module.css';

type DateDisplayFormat = 'YYYY-MM-DD' | 'DD/MM/YYYY';

interface MercuryDateInputProps<TFormValues extends FieldValues> {
  register: UseFormRegister<TFormValues>;
  watch: UseFormWatch<TFormValues>;
  name: Path<TFormValues>;

  label?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;

  error?: string | boolean | null;
  displayError?: boolean;

  min?: string;
  max?: string;

  displayFormat?: DateDisplayFormat;
  defaultValue?: string;

  disableWeekends?: boolean;
}

const formatDateForDisplay = (value: string, format: DateDisplayFormat) => {
  if (!value) return '';

  if (format === 'YYYY-MM-DD') return value;

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const parseOrNull = (value?: string): CalendarDate | null => {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
};

const isWeekend = (date: DateValue) => {
  const dayOfWeek = date.toDate(getLocalTimeZone()).getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

const CalendarIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ opacity: 0.7 }}
    aria-hidden="true"
  >
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4" />
    <path d="M4 9h16" />
    <path d="M8 13h2M12 13h2" />
  </svg>
);

type SingleCalendarState = ReturnType<typeof useCalendarState<CalendarDate, 'single'>>;

interface CalendarCellComponentProps {
  state: SingleCalendarState;
  date: CalendarDate;
}

const CalendarCellButton = ({ state, date }: CalendarCellComponentProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    isFocused,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);

  const isNonSelectable = isDisabled || isUnavailable;
  const isDateToday = isSameCalendarDayAsToday(date, state.timeZone);

  return (
    <div {...cellProps}>
      <Flex
        align="center"
        justify="center"
        {...buttonProps}
        ref={ref}
        className={`
          ${calendarClasses.datePickerCell}
          ${isSelected ? calendarClasses.selected : ''}
          ${isNonSelectable ? calendarClasses.disabled : ''}
          ${isFocused ? calendarClasses.focused : ''}
          ${isOutsideVisibleRange ? calendarClasses.outsideVisibleRange : ''}
          ${isDateToday && !isSelected ? calendarClasses.highlight : ''}
          ${!isSelected && !isNonSelectable ? calendarClasses.hovered : ''}
        `}
      >
        {formattedDate}
      </Flex>
    </div>
  );
};

interface SingleDateCalendarProps {
  value: CalendarDate | null;
  onChange: (value: CalendarDate) => void;
  minValue?: CalendarDate | null;
  maxValue?: CalendarDate | null;
  isDateUnavailable?: (date: DateValue) => boolean;
  ariaLabel?: string;
}

const SingleDateCalendar = ({
  value,
  onChange,
  minValue,
  maxValue,
  isDateUnavailable,
  ariaLabel,
}: SingleDateCalendarProps) => {
  const { locale } = useLocale();
  const state = useCalendarState<CalendarDate, 'single'>({
    value,
    onChange,
    minValue: minValue ?? undefined,
    maxValue: maxValue ?? undefined,
    isDateUnavailable,
    locale,
    createCalendar,
    selectionMode: 'single',
  });
  const ref = useRef<HTMLDivElement | null>(null);
  const { calendarProps, title } = useCalendar({ 'aria-label': ariaLabel }, state);
  const { gridProps, headerProps, weekDays, weeksInMonth } = useCalendarGrid({}, state);

  return (
    <Box
      {...calendarProps}
      ref={ref}
      role="region"
      aria-label={ariaLabel}
      className={calendarClasses.datePickerCalendarSurface}
    >
      <Group
        align="center"
        justify="space-between"
        wrap="nowrap"
        className={calendarClasses.datePickerHeader}
      >
        <UnstyledButton
          onClick={() => state.focusPreviousPage()}
          disabled={state.isPreviousVisibleRangeInvalid()}
          className={calendarClasses.datePickerHeaderButton}
          aria-label="Previous month"
        >
          <ChevronLeft />
        </UnstyledButton>
        <span aria-live="polite">{title}</span>
        <UnstyledButton
          onClick={() => state.focusNextPage()}
          disabled={state.isNextVisibleRangeInvalid()}
          className={calendarClasses.datePickerHeaderButton}
          aria-label="Next month"
        >
          <ChevronRight />
        </UnstyledButton>
      </Group>
      <Flex direction="column" {...gridProps} className={calendarClasses.datePickerCalendarGrid}>
        <div className={calendarClasses.datePickerGrid} {...headerProps}>
          {weekDays.map((day, index) => (
            <Flex
              // eslint-disable-next-line react/no-array-index-key
              key={`${day}-${index}`}
              align="center"
              justify="center"
              className={calendarClasses.datePickerWeekday}
            >
              {day}
            </Flex>
          ))}
        </div>
        <div className={calendarClasses.datePickerWeeks}>
          {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
            <div key={`week-${weekIndex}`} className={calendarClasses.datePickerGrid}>
              {state.getDatesInWeek(weekIndex).map((date, dayIndex) =>
                date ? (
                  <CalendarCellButton key={`${weekIndex}-${date.day}`} state={state} date={date} />
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${weekIndex}-empty-${dayIndex}`} />
                )
              )}
            </div>
          ))}
        </div>
      </Flex>
    </Box>
  );
};

const MercuryDateInput = <TFormValues extends FieldValues>({
  register,
  watch,
  name,
  label,
  description,
  required,
  disabled,
  hidden,
  error,
  displayError,
  min,
  max,
  displayFormat = 'YYYY-MM-DD',
  defaultValue,
  disableWeekends,
}: MercuryDateInputProps<TFormValues>) => {
  const [isOpen, setOpen] = useState(false);

  const wrapperError = useMemo(() => {
    if (displayError) return error;
    return error ? true : null;
  }, [displayError, error]);

  const watchedValue = watch(name) as unknown as string | undefined;
  const formValue = (watchedValue ?? '') as string;

  const resolvedValue = formValue || defaultValue || '';

  const displayValue = useMemo(
    () => formatDateForDisplay(resolvedValue, displayFormat),
    [resolvedValue, displayFormat]
  );

  const minCalendarDate = useMemo(() => parseOrNull(min), [min]);
  const maxCalendarDate = useMemo(() => parseOrNull(max), [max]);
  const selectedCalendarDate = useMemo(() => parseOrNull(resolvedValue), [resolvedValue]);

  const isDateUnavailable = useMemo(() => {
    if (!disableWeekends) return undefined;
    return (date: DateValue) => isWeekend(date);
  }, [disableWeekends]);

  const registration = defaultValue
    ? register(name, { value: defaultValue } as any)
    : register(name);

  const commitCalendarValue = (nextValue: CalendarDate) => {
    const iso = nextValue.toString();
    registration.onChange({
      target: { name, value: iso },
      type: 'change',
    } as unknown as React.ChangeEvent<HTMLInputElement>);
    setOpen(false);
  };

  if (hidden === true) return null;

  return (
    <Input.Wrapper
      label={label}
      description={description}
      withAsterisk={required}
      error={wrapperError}
      id={name}
      inputWrapperOrder={['label', 'error', 'input', 'description']}
      classNames={{ root: classes.inputWrapper }}
    >
      <Popover
        opened={isOpen}
        onChange={setOpen}
        position="bottom-start"
        withinPortal
        trapFocus
        classNames={{ dropdown: calendarClasses.datePickerDropdown }}
      >
        <Popover.Target>
          <Input
            component="div"
            role="combobox"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-required={required}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) setOpen((open) => !open);
            }}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
              if (disabled) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setOpen((open) => !open);
              }
            }}
            rightSection={<CalendarIcon size={16} />}
            rightSectionPointerEvents="none"
            classNames={{ input: classes.input }}
            styles={{
              input: {
                minHeight: rem(36),
                height: rem(36),
                fontSize: rem(14),
                backgroundColor: 'var(--ds-surface)',
                display: 'flex',
                alignItems: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
              },
            }}
          >
            {displayValue || <span style={{ color: 'var(--ds-grey-400)' }}>YYYY-MM-DD</span>}
          </Input>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Box className={calendarClasses.datePickerPopoverLayout}>
            <SingleDateCalendar
              value={selectedCalendarDate}
              onChange={commitCalendarValue}
              minValue={minCalendarDate}
              maxValue={maxCalendarDate}
              isDateUnavailable={isDateUnavailable}
              ariaLabel={label ? `${label} calendar` : 'Date picker calendar'}
            />
          </Box>
        </Popover.Dropdown>
      </Popover>

      <Box
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        <input type="hidden" {...registration} aria-hidden="true" tabIndex={-1} />
      </Box>
    </Input.Wrapper>
  );
};

export default MercuryDateInput;
